import TitleMenu from "components/Navigation/TitleMenu";
import NewProjectWidget from "components/NewProjectWidget";
import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";
//import ListView from "components/shared/list/listView";
import { PaginationToolbar } from "components/shared/list/paginationToolbar";
import { ListViewType, useList } from "components/shared/list/useListHook";
import Loader from "components/shared/loader";
import { SvgCommandDownload, SvgCommandDuplicate, SvgCommandEdit, SvgCommandEmail, SvgCommandReport, SvgEtap1, SvgInfo, SvgSave, SvgSuccess, SvgSupprimer } from "components/svg";
import { useCallback, useState } from "react";
import { useDeleteProjectMutation, useGetProjectsListQuery, useLazyLoadProjectQuery, useUpdateDublicateProjectMutation } from "store/api";
import useCustomForm from "utils/hooks/useCustomForm";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { env } from "env";
import { usePopup } from "providers/PopupProvider";
import { RenameProjectPopup } from "components/popups/RenameProjectPopup";
import { ProjectActionsPopup } from "components/popups/ProjectActionsPopup";
import { ConfirmationPopup } from "components/popups/ConfirmationPopup";
import NeedHelp from "components/NeedHelp";
import moment from "moment";
import {
    //type Profile,
    load as loadProjectAction,
    //goToStep as goToStepProjectAction
} from "store/projectSlice";

import { getBreadcrumbItems } from "configApp";
import { useProject } from "utils/hooks/useProject";
import { generateKey } from "utils/helpers";
import type { Project, ProjectVersionInfo } from "types/Project";
import { usePopover } from "providers/PopoverProvider";
import ContextMenuList from "components/ContextMenu/ContextMenuList";

// TODO: replace this with the real deployment date when communicated.
const OUTDATED_STUDY_CUTOFF_DATE = dayjs('2026-05-19').startOf('day');

const ProjectItemBlock: React.FC<{ item: any, index?: number }> = ({ item, index = 0 }) => {
    const { t, tHtml } = useTranslation();
    const [opacity, setOpacity] = useState<number>(0);
    const authState = useAppSelector((state) => state.auth);
    const { network } = useSalesNetwork();
    const [updateDublicateProject] = useUpdateDublicateProjectMutation();
    const [updateDeleteProject] = useDeleteProjectMutation();
    const { openPopup, closeLastPopup } = usePopup();
    const [projectService_load] = useLazyLoadProjectQuery();
    const dispatch = useAppDispatch();
    const { goToStep } = useProject();
    const { open } = usePopover();

    const DropdownContent: React.FC<{ item: any }> = ({ item }) => {
        const menuItems = [
            { text: t("UPDATE"), icon: <icon data-length="20" data-height="20"><SvgCommandEdit /></icon>, onClick: () => { openProject(item) } },
            (!item.data && item.versions) && { text: t('RESULTS_DUPLICATE'), icon: <icon data-length="20" data-height="20"><SvgCommandDuplicate /></icon>, onClick: () => { _duplicateProject(item) } },
            (!item.data) && { text: t("SHARE_STUDY"), icon: <icon data-length="20" data-height="20"><SvgCommandReport /></icon>, onClick: () => { window.location.href = shareProject(item) } },
            (!item.data && item._synthesisPath) && {
                element: (
                    <a
                        href={`${env.api_url}/upload/synthesis/${item._synthesisPath}`}
                        target="_blank" // NOSONAR
                        rel='noreferrer noopener' // NOSONAR
                        data-gap="5"
                        data-space="5"
                    >
                        <icon data-length="20" data-height="20" ><SvgSave /></icon>
                        <text data-space="5">{t("DOWNLOAD_SYNTHESIS")}</text>
                    </a>
                ),
            },
            { text: t('RESULTS_RENAME'), icon: <icon data-length="20" data-height="20"><SvgCommandEdit /></icon>, onClick: () => { renameProject(item) } },
            (!item.data && item._synthesisPath) && { text: t("SHARE_BY_MAIL"), icon: <icon data-length="20" data-height="20"><SvgCommandEmail /></icon>, onClick: () => { window.location.href = shareReport(item) } },
            (!item.data && item.user_id === authState.profile.id) && { separator: true },
            (!item.data && item.user_id === authState.profile.id) && { text: t("DELETE"), icon: "delete", onClick: () => { deleteProject(item) } },
        ].filter(Boolean) as any[];

        return (
            <ContextMenuList
                data-space="5"
                items={menuItems}
            />
        );
    };

    const actionsMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>, item: any) => {
        e.stopPropagation();
        open({
            content: <DropdownContent item={item} />,
            element: e.target as HTMLElement,
        });
    };

    const getProjectStatus = (project: Project) => {
        const items = getBreadcrumbItems(network?._parameters.disabledSteps);
        let step = items.find(item => item.index === project._maxReachedStep);
        if (!step) {
            step = items.find(item => item.index === items.length - 1);
        }
        if (step) {
            //project._stepLabel = t(step.label);
            return <>
                {step.index < 4 ?
                    <icon data-svg-size="24" data-svg-color="main"><SvgEtap1 /></icon>
                    :
                    <icon data-svg-size="24" data-svg-color="success-color"><SvgSuccess /></icon>}
                <text>{tHtml(step.label_new)}</text>
            </>;
        } else {
            return 'undefined'
        }
    };

    const onMouseLeave = useCallback(() => {
        setOpacity(0);
    }, [])
    const onMouseOver = useCallback(() => {
        setOpacity(90);
    }, [])

    const onConfirmOpenProject = useCallback(async (project: any) => {
        //setProject(project)

        const ProjectData = await projectService_load({ id: project?.id, uid: project?.uid }).unwrap();
        dispatch(loadProjectAction(ProjectData))
        const index: any = ProjectData.data?.currentStep;
        goToStep(index);
    }, [dispatch, projectService_load, goToStep])

    const openProject = useCallback((item: any) => {
        const createdAt = item?.createdAt ? dayjs(item.createdAt) : null;
        const isOutdatedStudy = createdAt ? createdAt.isBefore(OUTDATED_STUDY_CUTOFF_DATE, 'day') : false;

        if (isOutdatedStudy) {
            openPopup(
                <ConfirmationPopup
                    title={t("STUDY_OUTDATED_DATA_TITLE")}
                    message={t("STUDY_OUTDATED_DATA")}
                    icon="warning"
                    positiveCallback={() => {
                        onConfirmOpenProject(item);
                    }}
                    negativeCallBack={() => {
                    }}
                />
            );
        } else {
            onConfirmOpenProject(item);
        }
    }, [onConfirmOpenProject, openPopup, t])



    const _duplicateProject = useCallback((item: any) => {
        updateDublicateProject({ project: item })
    }, [updateDublicateProject])

    const shareProject = useCallback((item: any) => {
        const subject = t('SHARE_PROJECT_SUBJECT');
        // eslint-disable-next-line no-useless-concat
        const body = encodeURIComponent(t('SHARE_PROJECT_BODY') + "\n\n" + `${window.location.origin}/${network?.code}/load_project/${item.id}/${item._uid}`);
        return `mailto:?subject=${subject}&body=${body}`;
    }, [network, t])

    const shareReport = useCallback((item: any) => {
        const subject = t('SHARE_REPORT_SUBJECT');
        // eslint-disable-next-line no-useless-concat
        const body = encodeURIComponent(t('SHARE_REPORT_BODY') + "\n\n" + `${env.api_url}/upload/synthesis/${item._synthesisPath}`);
        return `mailto:?subject=${subject}&body=${body}`;
    }, [t])

    const deleteProject = useCallback((item: any) => {
        //alert("deleteProject" + item.id)
        openPopup(<ConfirmationPopup
            title={!item.versions ? t("DELETE_PROJECT") : t("ARE_YOU_SURE")}
            message={!item.versions ? t("DELETE_WARNING") : t("DELETE_WARNING_PROJECT")}
            icon="warning"
            positiveCallback={() => {
                updateDeleteProject({ project: item, includeVersions: item.versions });
            }}
            negativeCallBack={() => {
            }}
        />);
    }, [t, openPopup, updateDeleteProject])

    const renameProject = useCallback((item: any) => {
        openPopup(<RenameProjectPopup
            item={item}
            onClose={closeLastPopup}
        />);
    }, [openPopup, closeLastPopup])

    const openMobileActionsPopup = useCallback((item: any) => {
        openPopup(<ProjectActionsPopup
            item={item}
            onClose={closeLastPopup}
            openProject={openProject}
            duplicateProject={_duplicateProject}
            shareProject={shareProject}
            shareReport={shareReport}
            renameProject={renameProject}
            deleteProject={deleteProject}
            isOwner={item.user_id === authState.profile.id}
        />);
    }, [openPopup, closeLastPopup, openProject, _duplicateProject, shareProject, shareReport, renameProject, deleteProject, authState.profile.id])

    return (

        <>
            <group

                data-color="grey-color-dark"
                data-text-size="large"

                data-radius="10"
                data-adaptive="desktop"
            >
                {index > 0 && <group data-space-horizontal="20"><separator data-horizontal="" ></separator></group>}
                <group
                    data-wrap="no"
                    data-index={opacity === 0 ? "2" : "1"}
                    onClick={() => openProject(item)}

                >
                    <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space="20">
                        <text data-wrap="wrap" data-ellipsis="">{item?._reference}</text>
                    </group>
                    <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space="20">
                        <text data-wrap="wrap" data-ellipsis="">{item?._name}</text>
                    </group>
                    <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space="20">
                        <text data-wrap="wrap" data-ellipsis="">{item?._collaborateur}</text>
                    </group>
                    <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space="20">
                        <text data-wrap="wrap" data-ellipsis="">{item?._clientName}</text>
                    </group>
                    <group data-align="center" data-justify="center" data-length="autofit" data-contain="" data-gap="10" data-space="10">
                        <text data-wrap="wrap" data-ellipsis="">
                            {dayjs(item.createdAt).format("DD/MM/YYYY")}
                        </text>

                    </group>
                    <group data-align="center" data-justify="start" data-length="autofit" data-contain="" data-gap="10" data-space="20" data-wrap="no" data-max-length="150"    >
                        {getProjectStatus(item)}
                    </group>
                    <group data-align="center" data-justify="end" data-width="auto" data-contain="" data-gap="10" data-space="20"  >
                        <Button
                            icon="more_vert"
                            onClick={(e) => actionsMenu(e, item)}
                        >
                        </Button>

                    </group>

                </group>

            </group>
            <group

                data-color="grey-color-dark"
                data-radius="10"
                data-adaptive="mobile"
                data-text-size="large"

            >
                {index > 0 && <group data-space-horizontal="20"><separator data-horizontal="" data-color="main" data-opacity="30"></separator></group>}
                <group

                    data-index={opacity === 0 ? "2" : "1"}
                    onClick={() => openProject(item)}

                //data-adaptive="mobile"
                //data-max-length="500"
                >
                    <group
                        data-direction="column"
                        //data-gap="10"
                        data-space="20"
                        data-color="grey-color-dark"
                        data-text-size="medium"
                    >
                        <group data-align="center" data-length="autofit" data-contain="" data-gap="10" >
                            <text data-wrap="wrap">{item?._reference}</text>
                        </group>
                        <group data-align="center" data-length="autofit" data-contain="" data-gap="10" >
                            <text data-wrap="wrap">{item?._name}</text>
                        </group>
                        <group data-align="center" data-length="autofit" data-contain="" data-gap="10" >
                            <text data-wrap="wrap">{item?._collaborateur}</text>
                        </group>
                        <group data-align="center" data-length="autofit" data-contain="" data-gap="10" >
                            <text data-wrap="wrap">{item?._clientName}</text>
                        </group>
                        <group data-align="center" data-length="autofit" data-contain="" data-gap="10" >
                            <text data-wrap="wrap">
                                {dayjs(item.createdAt).format("DD/MM/YYYY")}
                            </text>
                        </group>
                        <group>
                            <separator data-horizontal=""></separator>
                            <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space="5" data-wrap="no">
                                {getProjectStatus(item)}
                            </group>
                            <group data-align="center" data-justify="end" data-width="auto" data-contain="" data-gap="10" data-space="5" >
                                <icon data-svg-size="24" data-svg-color="secondary" onClick={(e: any) => { e.stopPropagation(); openMobileActionsPopup(item); }}><SvgInfo /></icon>
                            </group>
                        </group>

                    </group>

                </group>

                <group
                    {...(opacity === 0 ? { "data-opacity": opacity } : {})}
                    data-radius="10" data-contain="" data-position="absolute" data-name="command_bar" data-space="20" data-height="fit"
                    data-background="secondary"
                    data-index={opacity === 0 ? "1" : "2"}
                    onMouseOut={onMouseLeave}
                    data-justify="center"
                //data-direction="column"
                //data-align="center"
                >
                    <Button
                        material={false}
                        icon="edit"
                        text={t("UPDATE")}
                        large
                        data-color="white"
                        data-interactive=""
                        data-transparent=""
                        onMouseOver={onMouseOver}
                        onClick={() => openProject(item)}
                    />

                    {!item.data && <Button
                        material={false}
                        icon="file_copy"
                        text={t("RESULTS_DUPLICATE")}
                        large
                        data-color="white"
                        data-interactive=""
                        data-transparent=""
                        onMouseOver={onMouseOver}
                        onClick={() => _duplicateProject(item)}
                    />}

                    {!item.data &&
                        <a
                            href={shareProject(item)}
                        >
                            <Button
                                material={false}
                                icon="file_copy"
                                text={t("SHARE_STUDY")}
                                large
                                data-color="white"
                                data-interactive=""
                                data-transparent=""
                                onMouseOver={onMouseOver}
                            />
                        </a>}

                    {!item.data && item._synthesisPath &&
                        <a
                            href={shareReport(item)}
                        >
                            <Button
                                material={false}
                                icon="file_copy"
                                text={t("SHARE_BY_MAIL")}
                                large
                                data-color="white"
                                data-interactive=""
                                data-transparent=""
                                onMouseOver={onMouseOver}
                            />
                        </a>}

                    {!item.data && item._synthesisPath &&
                        <a
                            href={`${env.api_url}/upload/synthesis/${item._synthesisPath}`}
                            target="_blank" // NOSONAR
                            rel='noreferrer noopener' // NOSONAR
                        >
                            <Button
                                material={false}
                                icon="file_copy"
                                text={t("GENERATE_SYNTHESIS")}
                                large
                                data-color="white"
                                data-interactive=""
                                data-transparent=""
                                onMouseOver={onMouseOver}
                            />
                        </a>}


                    {!item.data && item.user_id === authState.profile.id && <Button
                        material={false}
                        icon="delete"
                        text={t("DELETE")}
                        large
                        data-color="white"
                        data-interactive=""
                        data-transparent=""
                        onMouseOver={onMouseOver}
                        onClick={() => deleteProject(item)}
                    />}

                </group>
            </group>
        </>
    )
}

const ProjectItem: React.FC<{ data: Project }> = ({ data }) => {
    //const {t} = useTranslation();
    //const {network} = useSalesNetwork();


    return (
        <>

            <group
                data-contain=""
                data-cursor="pointer"
                data-index="2"
                data-direction="column"
                data-radius="10"
                data-color="main-light"
                data-adaptive="desktop"
                data-space="1"


            >
                <group
                    data-wrap="no"
                    data-border="inheret"
                    data-background="main-color-custom-lighter"
                    data-radius="10"
                    data-contain=""
                    data-direction="column"

                >
                    <group
                        data-wrap="no"
                        data-border="inheret"
                        data-radius="10"
                        data-color="main-light"
                        data-background="white"
                        data-direction="column"
                    >
                        <ProjectItemBlock item={data} index={0} key={generateKey()} />
                    </group>
                    <group
                        data-wrap="no"
                        data-color="main-light"
                        data-direction="column"
                    >
                        {

                            data?.versions?.map((item: ProjectVersionInfo, index: number) => {
                                return <ProjectItemBlock item={item} index={index} key={generateKey()} />
                            })
                        }
                    </group>
                </group>

            </group>

            <group
                data-contain=""
                data-cursor="pointer"
                data-index="2"
                data-direction="column"
                data-color="main-light"
                data-adaptive="mobile"
                data-space="1"
            >
                <group
                    data-wrap="no"
                    data-border="inheret"
                    data-radius="10"
                    data-contain=""
                    data-direction="column"
                    data-background="main-color-custom-lighter"
                >
                    <group
                        data-wrap="no"
                        data-border="inheret"
                        data-radius="10"
                        data-color="main-light"
                        data-background="white"
                        data-direction="column"
                    >
                        <ProjectItemBlock item={data} background="transparent" index={0} key={generateKey()} />
                    </group>
                    <group
                        data-wrap="no"
                        data-color="main-light"
                        data-direction="column"
                    >
                        {

                            data?.versions?.map((item: ProjectVersionInfo, index: number) => {
                                return <ProjectItemBlock item={item} background="main-lighter" index={index} key={generateKey()} />
                            })
                        }
                    </group>
                </group>

            </group>




        </>
    )
}

const MyProjectsPage: React.FC = () => {
    const { t } = useTranslation();

    const [search, setSearch] = useState<string>("");
    const [stepIndexes, setStepIndexes] = useState<number[]>();
    const { network } = useSalesNetwork();

    const { pagingProps, resetPagination /*, sort, setSort*/ } = useList({
        viewTypes: [ListViewType.Table],
        defaultPageSize: 25
    });
    const {
        pagination: { current, pageSize },
    } = pagingProps;
    //const [currentPageSize, setCurrentPageSize] = useState<number>(current)
    const steps = getBreadcrumbItems(network?._parameters.disabledSteps).map(step => { return { ...step, label: t(step.label) } });

    const {
        handleSubmit,
        control //, watch
    } = useCustomForm<any>({
        defaultValues: {
            q: ""
        },
    });
    const { data: ProjectsData, isLoading, isFetching } = useGetProjectsListQuery({
        code: network?.code,
        pageNumber: current || 1,
        pageSize: pageSize || 20,
        searchInput: search || "",
        stepIndexes: stepIndexes
    }, { skip: !network });

    return (
        <>

            <TitleMenu title={t("MMENU_MY_SIMULATION")}>
                <group
                    data-space-vertical="20"
                    //data-gap="30"
                    //data-wrap="no" 
                    data-align="center"
                >
                    <form
                        data-length="autofit"
                        data-type="group"
                        data-wrap="no"
                        onSubmit={handleSubmit((values) => {
                            console.log(values);
                            const _search = values.q;
                            const _stepIndexes = steps.filter(step =>
                                step.label.toLowerCase().includes(_search.toLowerCase()) ||
                                t(step.label_new).toLowerCase().includes(_search.toLowerCase())
                            ).map(step => step.index);
                            resetPagination();
                            if (_stepIndexes[0] === 4) _stepIndexes.push(5)
                            setStepIndexes(_stepIndexes);
                            //onSubmit(values);
                            setSearch(_search.toLowerCase());
                        })}
                    >
                        <Input
                            //{...register("q")}
                            customIcon={<space data-length="20"></space>}

                            //fit={true}
                            type={InputType.Text}
                            control={control}
                            name="q"
                            //defaultValue={search}
                            button={
                                <>
                                    <Button
                                        secondary
                                        large
                                        //adaptive
                                        type="submit"
                                        data-adaptive="desktop"
                                        //fit
                                        text={t("SEARCH_RE")}
                                        onClick={() => {
                                            //navigate(getAppUrl('/project/home/heat'))
                                            //onSubmit(searchValue);
                                        }}
                                    />
                                    <group data-width="auto" data-space-vertical="10" data-adaptive="mobile">
                                        <separator data-vertical="" data-height="30" data-opacity="0"></separator>
                                    </group>
                                    <Button
                                        //secondary
                                        //large
                                        mini
                                        //adaptive
                                        icon="search"
                                        type="submit"
                                        data-adaptive="mobile"
                                        //fit
                                        onClick={() => {
                                            //navigate(getAppUrl('/project/home/heat'))
                                            //onSubmit(searchValue);
                                        }}
                                    />
                                </>
                            }

                            labelSize={12}
                            fixLength="autofit"
                            dataLength="autofit"
                            placeholder={t("SEARCH_PROJECT")}

                        />
                    </form>

                    <space data-length="30" data-adaptive="desktop"></space>
                    <separator data-vertical="fit" data-adaptive="desktop"></separator>
                    <space data-length="30" data-adaptive="desktop"></space>
                    <separator data-horizontal="fit" data-margin="20" data-adaptive="mobile"></separator>

                    <group data-width="auto" data-length="forcefit">
                        <NewProjectWidget />
                    </group>



                </group>
            </TitleMenu>

            {ProjectsData && (ProjectsData?.total as number) > 0 &&
                <group
                    data-justify="center"
                    data-align="center"
                    data-gap="5"
                    data-index="2"
                    data-backdrop=""
                    data-wrap="no"
                    //-sticky-level="100"
                    //data-sticky=""
                    //data-sticky="top"
                    data-background="white"
                    data-adaptive="desktop"
                    data-color="grey-color-dark"
                    data-text-size="14"
                    data-margin-bottom="-20"
                    data-name="grid-header"
                    className="sticky"
                //className={`${isSticky ? "stuck" : ""}`}

                >
                    <group
                        data-wrap="no"
                        data-space="20"
                        data-space-vertical="15"
                        data-gap="20"
                        data-align="center"
                        data-max-length="1440"
                    >
                        <group data-wrap="no">
                            <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space-horizontal="20">
                                <text data-wrap="wrap">{t("PROJECT_REF")}</text>
                            </group>
                            <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space-horizontal="20">
                                <text data-wrap="wrap">{t("NEW_VERSION")}</text>
                            </group>
                            <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space-horizontal="20">
                                <text data-wrap="wrap">{t("PROJECT_COLLABORATEUR")}</text>
                            </group>
                            <group data-align="center" data-length="autofit" data-contain="" data-gap="10" data-space-horizontal="20">
                                <text data-wrap="wrap">{t("PROJECT_CLIENT")}</text>
                            </group>
                            <group data-align="center" data-length="autofit" data-justify="center" data-contain="" data-gap="10" data-space-right="30">
                                <text data-wrap="wrap">{t("PROJECT_DCREAT")}</text>
                            </group>
                            <group data-align="center" data-justify="start" data-length="autofit" data-contain="" data-gap="10" data-space-horizontal="20" data-max-length="150" >
                                {/* <space data-length="20" data-margin="2"></space> */}
                                <text data-wrap="wrap">{t("PROJECT_STATUS")}</text>
                            </group>
                            <group data-align="center" data-justify="end" data-width="auto" data-contain="" data-gap="10" data-space-horizontal="20">
                                <space data-length="20" data-margin="2"></space>
                            </group>


                        </group>
                    </group>

                </group>

            }


            <view
                data-height="auto"
                data-align="center"
                data-color="grey-color-dark"
                data-text-size="large"

            >
                <group
                    data-max-length="1440"
                    data-direction="column"
                >
                    {(isLoading || isFetching) ? <group data-min-height="400"><Loader /></group> :
                        <>
                            {ProjectsData && (ProjectsData?.total as number) === 0 &&

                                <group data-justify="center" data-align="center" data-direction="column" data-space="50">

                                    <text>{t("NO_PROJECT")}</text>
                                </group>
                            }
                            {/* <ListView
                                dataSource={(ProjectsData as ProjectList)?.projects}
                                view={ListViewType.Grid}
                                data-border="no"
                                keyField="id"
                                data-template="260"
                                scrollerProps={{ "data-space": "20" }}
                                listProps={{ "data-gap": "20" }}
                                itemComponent={ProjectItem}
                            /> */}

                            {ProjectsData && (ProjectsData?.total as number) !== 0 && <>
                                <group
                                    data-space="20"
                                >

                                    <group data-gap="20">
                                        {
                                            ProjectsData?.projects?.map((item: any) => {
                                                return (
                                                    <group key={generateKey()}>
                                                        <ProjectItem data={item} />
                                                    </group>)
                                            })
                                        }
                                    </group>
                                </group>


                                <group
                                    data-space="10"
                                    data-gap="20"
                                //data
                                >
                                    <PaginationToolbar
                                        data-space="10"
                                        data-gap="20"
                                        {...pagingProps}
                                        total={ProjectsData?.total}
                                    />
                                </group>
                            </>
                            }

                        </>
                    }
                </group>

                <NeedHelp />

            </view >

        </>
    )
}

export default MyProjectsPage;